import React, { useCallback, useEffect } from "react";
import {  useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog } from '@headlessui/react'
import Modal from "../../components/Modal";
import { Link } from "react-router-dom";
import {
    CheckCircleIcon,
    UserIcon,
    ClockIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    DocumentTextIcon,
    ClipboardIcon,
    ChevronLeftIcon,
    EyeIcon,
    WrenchScrewdriverIcon,
    QueueListIcon
} from '@heroicons/react/24/outline'
import { MapPinIcon as SolidMapPinIcon, CakeIcon } from '@heroicons/react/24/solid'
import api from "../../api";
import SimpleCombobox from "../../components/SimpleCombobox";
import InlineCheckbox from "../../components/InlineCheckbox"
import TextEditor from "../../components/TextEditor";
import debounce from "just-debounce-it";
import toast, { Toaster } from "react-hot-toast";
import { formatDateDDMMYYYY } from "../../util/formatDate";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import PatientModuleLayout from "../../components/patients/layout/PatientModuleLayout";
import PatientModuleHeader from "../../components/patients/layout/PatientModuleHeader";

const actions = [
    {
        name: 'Nueva consulta',
        description: 'Registrar una nueva consulta',
        background: "bg-green-100 dark:bg-green-900/40",
        href: "/new",
        icon: <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />,
    },
    {
        name: 'Datos del paciente',
        description: 'Ver y editar información',
        background: "bg-purple-100 dark:bg-purple-900/40",
        href: "/edit",
        icon: <UserIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
    },
    {
        name: 'Historial de consultas',
        description: 'Consultas anteriores',
        background: "bg-blue-100 dark:bg-blue-900/40",
        href: "/history",
        icon: <ClockIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
    },
    {
        name: 'Documentos adjuntos',
        description: 'Archivos y documentos',
        background: "bg-emerald-100 dark:bg-emerald-900/40",
        href: "/documents",
        icon: <DocumentIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
    },
    {
        name: 'Informes',
        description: 'Reportes del paciente',
        background: "bg-gray-100 dark:bg-gray-700/40",
        href: "/reports",
        icon: <DocumentTextIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
    },
]

const pages = [
    { name: 'Pacientes', href: '/patients', current: false },
    { name: 'Detalle  del paciente', href: `#`, current: true },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function PatientDetailPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarOpenDesktop, setSidebarOpenDesktop] = useState(true)
    const isDesktop = useMediaQuery("(min-width: 1024px)")
    const [cities, setCities] = useState([])
    const [patient, setPatient] = useState()
    const [doctors, setDoctors] = useState([])
    const [doctor, setDoctor] = useState(undefined)
    const [isUrgent, setIsUrgent] = useState(false)
    const [openConfirmation, setOpenConfirmation] = useState(false)
    const [openSelect, setOpenSelect] = useState(false)
    const [openSuccess, setOpenSuccess] = useState(false)
    const [error, setError] = useState('');
    const [errorMessage, setErrorMessage] = useState('')
    const [loading, setLoading] = useState(true)
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        observations: "",
        procedures: "",
    });
    const { idPatient } = useParams()

    useEffect(() => {
        if (isDesktop) {
            setSidebarOpen(false);
        }
    }, [isDesktop]);

    const handleSidebarToggle = () => {
        if (isDesktop) {
            setSidebarOpenDesktop(!sidebarOpenDesktop);
            return;
        }
        setSidebarOpen(!sidebarOpen);
    };

    const notify = (message, type = "success") => {
        toast(message, {
            style: {
                background: type === "success" ? "#4caf50" : "#f44336",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
            },
            duration: 4000,
        });
    };
        

    useEffect(() => {
        api.insurances.getMulti().then((res) =>{
            api.cities.getMulti().then((resp) => {
                setCities(resp);
                api.patients.getOne(idPatient).then((response) => {
                    setPatient({ ...response, name: response.firstName + " " + response.lastName, city: resp.find((city) => city.id === (response.city))?.name, insurance: res.find((insurance) => insurance.id === (response.insurance))?.name });
                    setFormData({
                        observations: response.observations,
                        procedures: response.procedures,
                    })
                    setOpen(!response.confirmedData);
                    setLoading(false);
                })
            })
        })
        api.users.getDoctors().then((response) => {
            setDoctors(response.map((element) => {
                const isLicenciado = element.role.toLowerCase() === "licenciado";
                const prefix = isLicenciado 
                    ? "Lic."
                    : element.sex.toLowerCase() === "masculino"
                        ? "Dr."
                        : "Dra.";
                return {
                    name: `${prefix} ${element.firstName} ${element.lastName}`,
                    ...element
                };
            }));
        });
    }, [])

    const debouncedPATCHData = useCallback(
    debounce(async (data) => {
        try {
            await api.patients.update(idPatient, data, true);
            notify("Datos actualizados correctamente", "success");
        } catch (error) {
            console.error(error);
            const message =
                error?.response?.data?.detail ||
                "Ocurrió un error al actualizar los datos";
            notify(message, "error");
        }
    }, 1000),
    []
);

    const handleChange = (name, value) => {
        setFormData((prev) => {
            const newData = { ...prev, [name]: value };
            debouncedPATCHData(newData);
            return newData;
        });
    };

    const handleDoctor = (field, value) => {
        setDoctor(value)
    }

    const handleIsUrgent = (field, value) => {
        setIsUrgent(value)
    }

    const handleSubmit = () => {
        api.turns.create({ doctorGovId: doctor.govId, patientGovId: patient.govId, isUrgent: isUrgent }).then(() => (setOpenConfirmation(false), setOpenSuccess(true))).catch((error) => error.code === 'serverError' ? (setErrorMessage('Error 500: Error del servidor'), setLoading(false)) : (setErrorMessage(error?.message), setLoading(false)))
    }

    useEffect(() => {
        if (!openConfirmation)
            setErrorMessage('')
    }, [openConfirmation])

    useEffect(() => {
        if (!openSelect)
            setError('')
    }, [openSelect])

    function formatManagementPlans(plans) {
    return plans
        .map((plan) => `${formatDateDDMMYYYY(plan.date)}: ${plan.description}`)
        .join("<br/>");
    }

    const validation = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Validacion de datos
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            Es necesario validar los datos del paciente para poder continuar
                        </label>
                        <div className="relative w-2/3 rounded-md mx-auto shadow-sm">
                        </div>
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => navigate(`/patients/${idPatient}/edit`)}
                >
                    Continuar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => navigate('/patients')}
                >
                    Volver
                </button>
            </div>
        </>
    )

    const confirmation = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Confirmación
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            ¿Está seguro de que quiere agregar el paciente al turnero?
                        </label>
                        <div className="relative w-2/3 rounded-md mx-auto">
                            {errorMessage && Object.keys(errorMessage).map((element) => (<p className="mt-2 text-sm text-red-600">{errorMessage[element]}</p>))}
                        </div>
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => handleSubmit()}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => (setOpenConfirmation(false), setOpenSelect(true))}
                >
                    Regresar
                </button>
            </div>
        </>
    )

    const selectDoctor = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Agregar al turnero
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            Seleccione el doctor que atenderá al paciente
                        </label>
                        <div className="flex gap-3 relative rounded-md mx-auto mt-3">
                            <SimpleCombobox value={doctor} containerClassName='w-2/3 shadow-sm' handleChange={handleDoctor} name="doctor" options={doctors} />
                            <InlineCheckbox containerClassName='relative flex pr-2 items-start mt-3' className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded" value={isUrgent} handleChange={handleIsUrgent} name='isUrgent' label='Es Urgencia' />
                        </div>
                        {error && <p className="mt-2 text-sm text-red-600">
                            {error}
                        </p>}
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => doctor ? (setOpenSelect(false), setOpenConfirmation(true)) : setError('Debe seleccionar un doctor para continuar')}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => (setDoctor(undefined), setOpenSelect(false))}
                >
                    Cancelar
                </button>
            </div>
        </>
    )

    const success = (
        <>
            < div >
                <div className="text-center">
                    <Dialog.Title as="h3" className="text-lg leading-6 font-semibold text-gray-900 dark:text-white">
                        Paciente agregado
                    </Dialog.Title>
                    <div className="mt-2 w-full">
                        <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-400">
                            El paciente fue agregado exitosamente al turnero.
                        </label>
                        <div className="relative w-2/3 rounded-md mx-auto shadow-sm">
                        </div>
                    </div>
                </div>
            </div >
            <div className="mt-5 sm:mt-6 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => setOpenSuccess(false)}
                >
                    Aceptar
                </button>
            </div>
        </>
    )

    return (
        <PatientModuleLayout
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            sidebarOpenDesktop={sidebarOpenDesktop}
            currentScreen="Pacientes"
            loading={loading}
            header={
                <PatientModuleHeader
                    pages={pages}
                    onToggleSidebar={handleSidebarToggle}
                />
            }
        >
                    <Modal size='sm:max-w-lg w-full' content={validation} open={open} setOpen={setOpen} />
                    <Modal size='sm:max-w-lg w-full' content={confirmation} open={openConfirmation} setOpen={setOpenConfirmation} />
                    <Modal size='sm:max-w-lg w-full' content={selectDoctor} open={openSelect} setOpen={setOpenSelect} />
                    <Modal size='sm:max-w-lg w-full' content={success} open={openSuccess} setOpen={setOpenSuccess} />

                    {patient && (
                    <>
                        {/* Patient info header */}
                        <div className="px-4 lg:px-10 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                            <div className="flex items-start gap-4">
                                {/* Avatar */}
                                <div className="flex h-12 w-12 lg:h-14 lg:w-14 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
                                    <UserIcon className="h-6 w-6 lg:h-7 lg:w-7 text-primary" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h1 className="text-xl lg:text-2xl font-bold leading-tight text-gray-900 dark:text-white truncate">{patient?.name}</h1>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">C.I. {patient?.govId || '--'}</p>
                                </div>
                            </div>
                            
                            {/* Mobile: card-style patient info */}
                            <div className="mt-4 lg:hidden">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="min-w-0 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Edad</span>
                                        <div className="mt-0.5 text-sm font-medium text-gray-800 dark:text-gray-200">{patient?.age ? `${patient.age} años` : '--'}</div>
                                    </div>
                                    <div className="min-w-0 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Ciudad</span>
                                        <div className="mt-0.5 text-sm font-medium text-gray-800 dark:text-gray-200">{patient?.city || '--'}</div>
                                    </div>
                                    {patient?.insurance && (
                                        <div className="min-w-0 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Seguro</span>
                                            <div className="mt-0.5 text-sm font-medium text-gray-800 dark:text-gray-200">{patient.insurance}</div>
                                        </div>
                                    )}
                                    <div className="min-w-0 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Últ. Visita</span>
                                        <div className="mt-0.5 text-sm font-medium text-gray-800 dark:text-gray-200">{patient?.lastAppointmentDate ? formatDateDDMMYYYY(patient.lastAppointmentDate) : '--'}</div>
                                    </div>
                                    <div className="min-w-0 rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Últ. Consulta</span>
                                        <div className="mt-0.5 text-sm font-medium text-gray-800 dark:text-gray-200">{patient?.lastConsultationDate ? formatDateDDMMYYYY(patient.lastConsultationDate) : '--'}</div>
                                    </div>
                                </div>
                                {patient?.firstDiagnosis && (
                                    <div className="mt-3">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                            <ClipboardIcon className="h-3.5 w-3.5 mr-1" />
                                            {patient.firstDiagnosis}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Desktop: inline patient info */}
                            <div className="hidden lg:flex flex-row flex-wrap items-center mt-3 gap-2">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                    <CakeIcon className="h-3.5 w-3.5" />
                                    {patient?.age ? `${patient.age} años` : "---"}
                                </span>
                                {patient?.city &&
                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                        <SolidMapPinIcon className="h-3.5 w-3.5" />
                                        {patient.city}
                                    </span>
                                }
                                {patient?.insurance &&
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                        Seguro: {patient.insurance}
                                    </span>
                                }
                                <span className="text-gray-300 dark:text-gray-600">|</span>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Últ. Visita: {patient?.lastAppointmentDate ? formatDateDDMMYYYY(patient.lastAppointmentDate) : '--'}
                                </span>
                                <span className="text-gray-300 dark:text-gray-600">|</span>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Últ. Consulta: {patient?.lastConsultationDate ? formatDateDDMMYYYY(patient.lastConsultationDate) : '--'}
                                </span>
                                {patient?.firstDiagnosis &&
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                                        <ClipboardIcon className="h-3.5 w-3.5 mr-1" />
                                        {patient.firstDiagnosis}
                                    </span>
                                }
                            </div>
                        </div>
                    <main className="flex-1 bg-gray-50 dark:bg-gray-800">
                        { (!/^\d+$/.test(patient.govId) || !patient.govId) &&
                            <Link to={`/patients/${idPatient}/edit`} className="flex gap-2 w-full items-center px-4 lg:px-10 py-3 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-b border-amber-200 dark:border-amber-800">
                                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0"/>
                                <p className="text-sm font-medium">Actualice el número de cédula del paciente</p>
                            </Link>
                        }
                        <div className="flex flex-col lg:flex-row w-full gap-6 px-4 lg:px-10 pt-6 lg:pt-8">
                            {/* Actions grid: 3 cols on mobile, single column list on desktop */}
                            <div className="grid grid-cols-3 gap-3 lg:flex lg:flex-col lg:gap-3 lg:w-72 lg:flex-shrink-0">
                                {actions.map((action) => (
                                    <div
                                        key={action.name}
                                        className="relative rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 lg:px-4 lg:py-3.5 shadow-sm flex flex-col lg:flex-row items-center lg:items-center gap-2 lg:gap-x-3 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer"
                                    >
                                        <div className={`flex-shrink-0 rounded-xl lg:rounded-lg p-2.5 lg:p-2 ${action.background}`}>
                                            {action.icon}
                                        </div>
                                        <div className="flex-1 min-w-0 text-center lg:text-left">
                                            <Link to={`/patients/${idPatient}${action.href}`} className="focus:outline-none">
                                                <span className="absolute inset-0" aria-hidden="true" />
                                                <p className="text-xs lg:text-sm font-semibold text-gray-900 dark:text-white leading-tight">{action.name}</p>
                                                <p className="hidden lg:block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{action.description}</p>
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                                <div
                                    key='Agregar paciente al turnero'
                                    className="relative rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 lg:px-4 lg:py-3.5 shadow-sm flex flex-col lg:flex-row items-center lg:items-center gap-2 lg:gap-x-3 hover:border-red-300 hover:shadow-md transition-all cursor-pointer"
                                >
                                    <div className='flex-shrink-0 rounded-xl lg:rounded-lg p-2.5 lg:p-2 bg-red-100 dark:bg-red-900/40'>
                                        <DocumentIcon className="h-6 w-6 text-red-500 dark:text-red-400" />
                                    </div>
                                    <div className="flex-1 min-w-0 text-center lg:text-left">
                                        <button onClick={() => setOpenSelect(true)} className="focus:outline-none">
                                            <span className="absolute inset-0" aria-hidden="true" />
                                            <p className="text-xs lg:text-sm font-semibold text-gray-900 dark:text-white leading-tight">Agregar al turnero</p>
                                            <p className="hidden lg:block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Enviar al turnero de atención</p>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="w-full flex flex-col gap-4 lg:gap-5">
                                {/* Observaciones */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/40">
                                            <EyeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Observaciones</h3>
                                    </div>
                                    <div className="p-4">
                                        <TextEditor 
                                            name="observations"
                                            label=""
                                            value={formData.observations}
                                            handleChange={handleChange}
                                            error={null}
                                            required={false}
                                            containerClassName="!mb-0"
                                            readOnly={false}
                                            height={140}
                                        />
                                    </div>
                                </div>
                                {/* Procedimientos */}
                                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                                            <WrenchScrewdriverIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Procedimientos</h3>
                                    </div>
                                    <div className="p-4">
                                        <TextEditor 
                                            name="procedures"
                                            label=""
                                            value={formData.procedures}
                                            handleChange={handleChange}
                                            error={null}
                                            required={false}
                                            containerClassName="!mb-0"
                                            height={140}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Historial de conductas */}
                        <div className="px-4 lg:px-10 mt-5 pb-8">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50">
                                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/40">
                                        <QueueListIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                    </div>
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Historial de conductas</h3>
                                    <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">Solo lectura</span>
                                </div>
                                <div className="p-4">
                                    <TextEditor 
                                        name="managementPlans"
                                        label=""
                                        value={formatManagementPlans(patient.managementPlans)}
                                        handleChange={() => {}}
                                        error={null}
                                        required={false}
                                        containerClassName="!mb-0"
                                        readOnly={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </main>
                    </>
                    )}
            <Toaster />
        </PatientModuleLayout>
    );
}
